import { useCallback, useContext, useMemo, useRef, useState } from "react";
import AuthContext from "../../../context/AuthContext";
import { withLogisticsIdentity } from "../../../utils/logisticsProgressLookup";
import { isTrackedLogisticsAsset } from "../models/logisticsProgressPayload";
import { LogisticsUpdateLauncherContext } from "./logisticsUpdateLauncherContext";

// Loaded on first use, so screens that never open an update do not pay for the dialog. It is put
// in state when it arrives, not rendered through React.lazy and Suspense: on the Plants page a
// suspended dialog was never retried, so it never appeared.
const loadDialog = () => import("../components/stageUpdates/SingleAssetLogisticsUpdate").then((module) => module.default);

// The dialog lists an asset by tag and name; cards name their items differently per type.
const toUpdateAsset = (item) => ({
  ...withLogisticsIdentity(item),
  tagNumber: item.tagNumber || "",
  equipmentName:
    item.equipmentName || item.tierName || item.componentName || item.instrument_type_details?.name || item.name || "",
});

/**
 * Lets any asset card start a logistics progress update for its asset. Mounted once for the app:
 * it owns the one dialog, so no screen has to pass a handler down to its cards.
 *
 * Staff only, and only for assets Logistics tracks with their progress merged in.
 */
export function LogisticsUpdateLauncherProvider({ children }) {
  const { user } = useContext(AuthContext);
  const isStaff = user?.is_staff === true;
  const [asset, setAsset] = useState(null);
  const [Dialog, setDialog] = useState(null);
  const listenersRef = useRef(new Set());

  const canUpdate = useCallback(
    (item) => isStaff && isTrackedLogisticsAsset(item) && item.id != null && item.id !== "" && Boolean(item.logisticsStage),
    [isStaff],
  );
  const open = useCallback(
    (item) => {
      if (!canUpdate(item)) return;
      setAsset(toUpdateAsset(item));
      // A component is a function, so it is stored through an updater.
      loadDialog().then((component) => setDialog(() => component));
    },
    [canUpdate],
  );
  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);
  const notifyUpdated = useCallback(() => listenersRef.current.forEach((listener) => listener()), []);
  const close = useCallback(() => setAsset(null), []);

  const value = useMemo(() => ({ canUpdate, open, subscribe }), [canUpdate, open, subscribe]);

  return (
    <LogisticsUpdateLauncherContext.Provider value={value}>
      {children}
      {asset && Dialog ? <Dialog asset={asset} isStaff={isStaff} onClose={close} onUpdated={notifyUpdated} /> : null}
    </LogisticsUpdateLauncherContext.Provider>
  );
}
